import type { EventCallbacks } from '@moonpig/launchpad-analytics'
import { PageTypeEnum, ProductInfoProduct } from '../../types'
import { getPrimaryButtonText } from './getPrimaryButtonText'
import { useProductsLocaleText } from '../../locale'

export type ButtonHandlers = EventCallbacks<HTMLButtonElement>

type ButtonConfig = {
  handlers: ButtonHandlers
  text: string
}

type Buttons = {
  primaryButton: ButtonConfig
  secondaryButton?: ButtonConfig
}

type CreateButtonsArgs = {
  pageType: PageTypeEnum
  productType: string | undefined
  product: ProductInfoProduct
  groupCardProjectId: string | undefined
  localiseProductsText: ReturnType<typeof useProductsLocaleText>
  buttonActions: {
    personalise: () => void
    addDigitalGiftToBasket: () => void
    addCrossSellGiftToBasket: () => void
    addBasketCrossSellGiftToBasket: () => void
    onAddToBasket: () => void
    createGroupCard: () => void
  }
  buttonHandlers: {
    personalise: ButtonHandlers
    leaveItBlank: ButtonHandlers
  }
  showSecondaryButton: boolean
  hasDigitalGift?: boolean
}

export const createButtons = ({
  pageType,
  productType,
  product,
  groupCardProjectId,
  localiseProductsText,
  buttonActions,
  buttonHandlers,
  showSecondaryButton,
  hasDigitalGift,
}: CreateButtonsArgs): Buttons => {
  const primaryButtonText = getPrimaryButtonText({
    product,
    productType,
    groupCardProjectId,
    localiseText: localiseProductsText,
  })

  switch (pageType) {
    case PageTypeEnum.ADD_A_CARD: {
      const getAddACardOnClick = () => {
        if (productType === 'groupCard' || groupCardProjectId)
          return buttonActions.createGroupCard
        if (hasDigitalGift) return buttonActions.addDigitalGiftToBasket
        return buttonActions.personalise
      }

      return {
        primaryButton: {
          handlers: { onClick: getAddACardOnClick() },
          text: primaryButtonText,
        },
      }
    }
    case PageTypeEnum.ADD_A_GIFT:
      return {
        primaryButton: {
          handlers: { onClick: buttonActions.addCrossSellGiftToBasket },
          text: localiseProductsText(
            product.customisable
              ? 'common.personalise'
              : 'common.add_to_basket',
          ),
        },
      }
    case PageTypeEnum.BASKET:
      return {
        primaryButton: {
          handlers: { onClick: buttonActions.addBasketCrossSellGiftToBasket },
          text: primaryButtonText,
        },
      }
    default:
      if (!product.customisable)
        return {
          primaryButton: {
            handlers: { onClick: buttonActions.onAddToBasket },
            text: primaryButtonText,
          },
        }

      if (productType === 'groupCard' || groupCardProjectId)
        return {
          primaryButton: {
            handlers: { onClick: buttonActions.createGroupCard },
            text: primaryButtonText,
          },
        }

      return {
        primaryButton: {
          handlers: buttonHandlers.personalise,
          text: primaryButtonText,
        },
        ...(showSecondaryButton && {
          secondaryButton: {
            handlers: buttonHandlers.leaveItBlank,
            text: localiseProductsText('common.card_add_to_basket_cta'),
          },
        }),
      }
  }
}
